import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IFeedbackData, IFetchResult } from '../models';
import { DEFAULT_ENV } from '../const';
import { FeedbackFormResult } from '../_Result/FeedbackFormResult';
import { buildLegoElems, getProductBundleRequest, getProductRequest, prepareDialogData } from '../utils';
import { AppContext } from '@src/providers/App';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { getCenLead } from '../../../utils/analytics';
import { Dialog } from '@src/atoms/Dialog';
import { Unit } from '@src/core/Unit';
import { Box } from '@src/atoms/Box';
import { IProduct, IProductBundle } from '@src/models/entities';
import { classnames } from '@src/utils/classnames';

import styles from './FeedbackFormContainer.module.scss';
import { Lego } from '@src/molecules/Lego';
import { AnyType } from '@src/models/common';
import { useHasUserTag } from '@src/hooks/useHasUserTag';

interface IProps {
  data: IFeedbackData;
  onClose: () => void;
}

export const FeedbackFormContainer: FC<IProps> = ({ data, onClose }) => {
  const { ngApiClient, isAuthZone } = useContext(AppContext);
  const { isMobile } = useContext(BreakpointsContext);
  const isDemoUser = useHasUserTag('demoUser');
  const [loading, setLoading] = useState(false);
  const [fetchResult, setFetchResult] = useState<IFetchResult | null>(null);
  const [product, setProduct] = useState<IProduct | null>(null);
  const [productBundle, setProductBundle] = useState<IProductBundle | null>(null);
  const [env, setEnv] = useState<{ [x: string]: string }>(DEFAULT_ENV);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit = async (formData) => {
    const basicFetchResult = { onClose: handleClose };
    setEnv({
      action: 'validation_success',
    });

    try {
      if (data && formData) {
        setLoading(true);
        const { feedbackFormType } = data;
        const requestData = prepareDialogData(feedbackFormType, {
          ...data.data,
          ...data.defaultValue,
          ...formData,
          referer: window.location.href,
          productName: product?.productName,
          productId: product?.productId,
          cenLead: getCenLead(),
        });
        const response = await ngApiClient.mutate('callToActionRequest', requestData);
        setLoading(false);
        if (response?.data.results?.[0]?.errors) {
          setFetchResult({ type: 'error', ...basicFetchResult });
          setEnv({
            action: 'requestError',
            details: JSON.stringify(response.data.results?.[0]?.errors),
          });
          setLoading(false);
        } else {
          setFetchResult({ type: 'success', ...basicFetchResult });
          setEnv({
            action: 'requestSuccess',
          });
        }
      }
    } catch (e) {
      setFetchResult({ type: 'error', ...basicFetchResult });
      setEnv({
        action: 'requestError',
        details: (e as AnyType).toString(),
      });
    }
  };

  useEffect(() => {
    const loadProduct = async (id: number) => {
      try {
        setLoading(true);
        const productResponse = await ngApiClient.get('reportProduct', getProductRequest(id));
        const newProduct = productResponse.data?.page?.entries?.[0] || null;

        setProduct(newProduct);
      } finally {
        setLoading(false);
      }
    };
    const loadBundle = async (id: number) => {
      try {
        setLoading(true);
        const productResponse = await ngApiClient.get('productBundle', getProductBundleRequest(id));
        const newProductBundle = productResponse.data?.page?.entries?.[0] || null;

        setProductBundle(newProductBundle);
      } finally {
        setLoading(false);
      }
    };

    if (data?.data?.productId) {
      loadProduct(data.data.productId);
    }
    if (data?.data?.productBundleId) {
      loadBundle(data.data.productBundleId);
    }
  }, [data.data?.productBundleId, data.data?.productId, ngApiClient]);
  const isFatForm = ['requestEdu', 'requestProductFilter'].includes(data.feedbackFormType);

  const containerClass = classnames({
    [styles.root]: !isMobile,
    [styles.fetchResult]: isMobile && !!fetchResult,
    // Форму с датой делаем шире
    [styles.rootFat]: isFatForm && !isMobile,
    [styles.rootTariff]: data.feedbackFormType === 'requestTariff' && !isMobile,
  });

  const unitEnv = useMemo(() => {
    const newUnitEnv = env;
    if (product?.productId) {
      newUnitEnv.productId = `${product?.productId}`;
    }
    if (data?.feedbackFormType) {
      newUnitEnv.formId = `${data?.feedbackFormType}`;
    }

    return newUnitEnv;
  }, [data?.feedbackFormType, env, product?.productId]);

  if (!data) return null;

  const elems = buildLegoElems(
    data.feedbackFormType,
    product,
    productBundle,
    data?.data?.tariff,
    data?.data?.services,
    isAuthZone,
    loading,
    isMobile,
    isDemoUser,
  );
  return (
    <Dialog
      id={data.feedbackFormType}
      open={!!data}
      onClose={handleClose}
      className={containerClass}
      importanceLevel={data?.importanceLevel}
      disableCloseBtn={false}
    >
      {!fetchResult && <Dialog.Header className={isMobile ? styles.mobileDialog : styles.dialog} />}
      <Unit id={'FeedbackForm'} env={unitEnv}>
        <Box>
          {fetchResult ? (
            <FeedbackFormResult {...fetchResult} feedbackFormType={data.feedbackFormType} />
          ) : (
            <Lego elems={elems} isNewForm={true} onSubmit={onSubmit} boxWrap={{ column: true }} />
          )}
        </Box>
      </Unit>
    </Dialog>
  );
};
